import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import AddUserColor from '@/assets/icons_new/AddUserColor.svg'
import { AddUserModal, type UserFormValues } from '@/modules/settings/components/modal/ModalUsers'
import { ImportUserModal } from '@/modules/users/components/ImportUserModal'
import {
  useMutationAdminUserCreate,
  useMutationImportUser,
  UserListRequestPayload,
  CreateUserError,
} from '@/lib/api/company/configuration/adminPanel/users'
import { useQueryCompanyRoles } from '@/lib/api/company/roles'
import { useQueryDrivers } from '@/lib/api/company/drivers'

interface UsersBasicActionBarProps {
  companyId: string
}

const UsersBasicActionBar: React.FC<UsersBasicActionBarProps> = ({ companyId }) => {
  const { t } = useLocalize()
  const [showAddModal, setShowAddModal] = React.useState(false)
  const [showImportModal, setShowImportModal] = React.useState(false)
  const [createError, setCreateError] = React.useState<string | null>(null)

  const [filters] = React.useState<UserListRequestPayload>({
    take: 20,
    skip: 0,
  })

  const { data: rolesData } = useQueryCompanyRoles(companyId)
  
  const rolesOptions = React.useMemo(() => {
    return rolesData?.roles.map(role => ({
      value: role.id.toString(),
      label: role.name,
      isDriver: role.isDriver,
    })) || []
  }, [rolesData])

  const driversQuery = useQueryDrivers({ companyId })
  const driversOptions = React.useMemo(() => {
    const data = driversQuery.data
    if (!data) return []
    const list = Array.isArray(data) ? data : (data as any).drivers ?? []
    return list.map((d: { id: number; name: string; surname: string }) => ({
      value: String(d.id),
      label: `${d.name} ${d.surname}`,
    }))
  }, [driversQuery.data])

  const mutationCreate = useMutationAdminUserCreate(companyId)
  const mutationImport = useMutationImportUser(companyId)

  const handleCreateSubmit = async (values: UserFormValues) => {
    setCreateError(null)
    try {
      await mutationCreate.mutateAsync({
        payload: {
          login: values.login,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.roleId ? { id: values.roleId } : undefined,
          driverId: values.driverId ? parseInt(values.driverId, 10) : undefined,
        },
        filters: filters,
      })
      setShowAddModal(false)
    } catch (error) {
      if (error instanceof CreateUserError) {
        setCreateError(error.message)
      } else if (error instanceof Error) {
        setCreateError(error.message)
      } else {
        setCreateError(t('usersModule.errors.createFailed'))
      }
    }
  }

  const handleImportSubmit = async (key: string) => {
    try {
      await mutationImport.mutateAsync({ key })
      setShowImportModal(false)
    } catch (error) {
      console.error('Error importing user:', error)
    }
  }

  return (
    <>
      <div className="tw-flex tw-items-center tw-justify-start tw-gap-2 tw-border-b tw-border-gray-200 tw-bg-content-primary tw-px-4 tw-py-1.5">
        <div
          className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-p-2 hover:tw-bg-base-300 hover:tw-opacity-90"
          onClick={() => {
            setCreateError(null)
            setShowAddModal(true)
          }}
        >
          <img src={AddUserColor} alt="Add User Icon" />
          <p className="tw-text-sm tw-font-medium tw-text-primary">
            {t('usersModule.addUser')}
          </p>
        </div>
      </div>

      <AddUserModal
        isOpen={showAddModal}
        rolesOptions={rolesOptions}
        driversOptions={driversOptions}
        pending={mutationCreate.isPending}
        error={createError}
        onCancel={() => {
          setCreateError(null)
          setShowAddModal(false)
        }}
        onSubmit={handleCreateSubmit}
      />

      <ImportUserModal
        isOpen={showImportModal}
        pending={mutationImport.isPending}
        onCancel={() => setShowImportModal(false)}
        onSubmit={handleImportSubmit}
      />
    </>
  )
}

export default UsersBasicActionBar

